import {
  DEFAULT_LATE_REMINDER_MAX,
  IReminderTemplate,
  IReminderTemplateV2,
  ITaskTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { sortBy, times } from 'lodash';
import { Bell, PlusCircle, Trash2 } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import shortid from 'shortid';
import { Label } from '../../../../../../components/CustomInputLabel';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { ONE_DAY, toWeekAndDayDuration } from '../../../../../../services/time';
import { TaskBuilderContext } from '../types';
import * as UX from '../../newux/kit';
import { ClassicBlue600 } from './classicBlue600';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';

const NewUxReminderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const NewUxIndex = styled('span')`
  width: 22px;
  height: 22px;
  flex: none;
  border-radius: 50%;
  background: ${(p) => p.theme.custom.colors.gray.w100};
  color: ${(p) => p.theme.custom.colors.gray.w700};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NewUxAddRow = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #0061c4;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
`;

const NewUxInline = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: ${(p) => p.theme.custom.colors.gray.w700};
`;

const ICON = Bell;

const ReminderRowV2 = styled('div')`
  display: grid;
  grid-template-columns: ${(p) => p.theme.spacing(3)}px ${(p) =>
      p.theme.spacing(6.5)}px max-content 16px;
  grid-column-gap: ${(p) => p.theme.spacing(1)}px;
  align-items: center;
  width: 100%;
`;

const ReminderRowReadOnly = styled('div')`
  display: grid;
  grid-template-columns: max-content 4fr;
  grid-gap: ${(p) => p.theme.spacing(2)}px;
  margin-bottom: ${(p) => p.theme.spacing(2)}px;
  align-items: center;
`;

const ReminderCount = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(p) => p.theme.spacing(3)}px;
  width: ${(p) => p.theme.spacing(3)}px;
  padding: ${(p) => p.theme.spacing(0.5)}px;
  background: ${(p) => p.theme.custom.colors.gray.w300};
  border-radius: 50%;
  font-size: ${(p) => p.theme.custom.fontSize.s}px;
  font-weight: ${(p) => p.theme.typography.fontWeightMedium};
  line-height: 1;
  text-align: center;
`;

const ReminderV2 = ({
  value,
  onChange,
  i
}: {
  value: IReminderTemplateV2;
  onChange: (nextValue: IReminderTemplateV2 | null) => void;
  i: number;
}) => {
  const { t } = useTranslation();
  const days = Math.round(Math.abs(value.relativeDistanceToDeadline) / ONE_DAY);
  return (
    <ReminderRowV2>
      <ReminderCount>{i + 1}</ReminderCount>
      <TextField
        type="number"
        value={days}
        variant="outlined"
        inputProps={{ min: 1 }}
        onChange={(ev) => {
          const d = +ev.target.value;
          const nextValue = d * ONE_DAY;
          onChange({
            ...value,
            relativeDistanceToDeadline: -nextValue
          });
        }}
      />

      <div>
        {days === 1 ? t('day before deadline') : t('days before deadline')}
      </div>
      <Tooltip title={'Remove reminder'}>
        <IconButton onClick={() => onChange(null)}>
          <Trash2 size={16} />
        </IconButton>
      </Tooltip>
    </ReminderRowV2>
  );
};

const sortReminders = (reminders: IReminderTemplate[]) => {
  return sortBy(reminders, (r) => {
    return -(r.beforeEnd || 0);
  });
};

export const RemindersReadOnly = ({
  value
}: {
  value: IReminderTemplate[];
}) => {
  const { t } = useUnsafeTranslation();
  const sortedReminders = useMemo(() => sortReminders(value), [value]);
  if (!sortedReminders.length) {
    return <div>{t('No reminders')}</div>;
  }
  return (
    <>
      {sortedReminders.map((r, i) => {
        return (
          <ReminderRowReadOnly>
            <ReminderCount>{i + 1}</ReminderCount>
            <div>
              {toWeekAndDayDuration(r.beforeEnd || 0, t)} {t('beforeDeadline')}
            </div>
          </ReminderRowReadOnly>
        );
      })}
    </>
  );
};

const RemindersV2 = React.memo(
  ({
    value,
    onChange
  }: {
    value: IReminderTemplateV2[];
    onChange: (nextReminders: IReminderTemplateV2[]) => void;
  }) => {
    return (
      <>
        {value.map((r, i) => (
          <ReminderV2
            key={r.id}
            value={r}
            onChange={(nextR) => {
              const nextReminders = [...value];
              if (nextR === null) {
                nextReminders.splice(i, 1);
              } else {
                nextReminders[i] = nextR;
              }

              onChange(nextReminders);
            }}
            i={i}
          />
        ))}
      </>
    );
  }
);

const AddReminderButtonV2 = React.memo(
  ({
    value,
    onChange
  }: {
    value: IReminderTemplateV2[];
    onChange: (nextReminders: IReminderTemplateV2[]) => void;
  }) => {
    const { t } = useTranslation();
    return (
      <Button
        size="small"
        color="primary"
        startIcon={<PlusCircle size={20} />}
        onClick={() => {
          onChange([
            ...value,
            {
              id: shortid(),
              relativeDistanceToDeadline: -ONE_DAY,
              v: 2
            }
          ]);
        }}
      >
        {t('Add Reminder')}
      </Button>
    );
  }
);

export const TaskSettingsOverviewSectionRemindersV2 = ({
  title,
  reminders,
  onEdit,
  divider
}: {
  title: string;
  reminders: IReminderTemplateV2[];
  onEdit?: () => void;
  divider?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const {
    preDeadlineReminders,
    onDeadlineReminder,
    afterDeadlineRemindersCount
  } = useMemo(() => {
    const preDeadlineReminders = sortBy(
      reminders.filter((r) => r.relativeDistanceToDeadline < 0),
      (r) => r.relativeDistanceToDeadline
    );
    const onDeadlineReminder = !!reminders.find(
      (r) => r.relativeDistanceToDeadline === 0
    );
    const afterDeadlineRemindersCount = reminders.filter(
      (r) => r.relativeDistanceToDeadline > 0
    ).length;
    return {
      preDeadlineReminders,
      onDeadlineReminder,
      afterDeadlineRemindersCount
    };
  }, [reminders]);

  const hasAnyReminders = preDeadlineReminders.length > 0 || onDeadlineReminder || afterDeadlineRemindersCount > 0;

  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      {!hasAnyReminders && <div>{t('No reminders')}</div>}
      {preDeadlineReminders.map((r) => (
        <div key={r.id}>
          {toWeekAndDayDuration(Math.abs(r.relativeDistanceToDeadline || 0), t)}{' '}
          {t('beforeDeadline')}
        </div>
      ))}
      {onDeadlineReminder && <div>{t('On deadline')}</div>}
      {afterDeadlineRemindersCount === 1 && (
        <div>{t('1 time after deadline')}</div>
      )}
      {afterDeadlineRemindersCount > 1 && (
        <div>
          {t('{{ count }} times after deadline', {
            count: afterDeadlineRemindersCount
          })}
        </div>
      )}
    </TaskSettingsOverviewSection>
  );
};

/**
 * NEW_UX read-only reminders summary — a bulleted list under a "Reminders"
 * label (task viewer, img4). Same content as TaskSettingsOverviewSectionRemindersV2,
 * rendered as an unordered list per the new design.
 */
export const TaskViewerReminders = ({
  reminders
}: {
  reminders: IReminderTemplateV2[];
}) => {
  const { t } = useUnsafeTranslation();
  const {
    preDeadlineReminders,
    onDeadlineReminder,
    afterDeadlineRemindersCount
  } = useMemo(() => {
    const preDeadlineReminders = sortBy(
      reminders.filter((r) => r.relativeDistanceToDeadline < 0),
      (r) => r.relativeDistanceToDeadline
    );
    const onDeadlineReminder = !!reminders.find(
      (r) => r.relativeDistanceToDeadline === 0
    );
    const afterDeadlineRemindersCount = reminders.filter(
      (r) => r.relativeDistanceToDeadline > 0
    ).length;
    return {
      preDeadlineReminders,
      onDeadlineReminder,
      afterDeadlineRemindersCount
    };
  }, [reminders]);

  const hasAnyReminders =
    preDeadlineReminders.length > 0 ||
    onDeadlineReminder ||
    afterDeadlineRemindersCount > 0;

  return (
    <div>
      <UX.FieldLabel>{t('Reminders')}</UX.FieldLabel>
      {!hasAnyReminders ? (
        <div style={{ color: UX.tokens.color.grey700 }}>{t('No reminders')}</div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, color: UX.tokens.color.grey700 }}>
          {preDeadlineReminders.map((r) => (
            <li key={r.id}>
              {toWeekAndDayDuration(
                Math.abs(r.relativeDistanceToDeadline || 0),
                t
              )}{' '}
              {t('beforeDeadline')}
            </li>
          ))}
          {onDeadlineReminder && <li>{t('On deadline')}</li>}
          {afterDeadlineRemindersCount === 1 && (
            <li>{t('1 time after deadline')}</li>
          )}
          {afterDeadlineRemindersCount > 1 && (
            <li>
              {t('{{ count }} times after deadline', {
                count: afterDeadlineRemindersCount
              })}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const FullWidthContainer = styled('div')((p) => ({
  width: '100%'
}));

export const TaskSettingsReminders = ({
  title,
  description,
  onBack,
  value,
  onChange,
  newUx = false
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { remindersV2 } = value;
  const reminders = useMemo(() => {
    const preDeadlineReminders = sortBy(
      remindersV2.filter((r) => r.relativeDistanceToDeadline < 0),
      (r) => r.relativeDistanceToDeadline
    );
    const onDeadlineReminder = !!remindersV2.find(
      (r) => r.relativeDistanceToDeadline === 0
    );
    const afterDeadlineReminders = sortBy(
      remindersV2.filter((r) => r.relativeDistanceToDeadline > 0),
      (r) => r.relativeDistanceToDeadline
    );
    const afterDeadlineRemindersInterval =
      afterDeadlineReminders[0]?.relativeDistanceToDeadline || 0;
    const afterDeadlineRemindersCount = afterDeadlineReminders.length;
    return {
      preDeadline: preDeadlineReminders,
      onDeadline: onDeadlineReminder,
      afterDeadline: {
        interval: afterDeadlineRemindersInterval,
        count: afterDeadlineRemindersCount
      }
    };
  }, [remindersV2]);

  const updateRemindersV2 = (
    updateFn: (rs: IReminderTemplateV2[]) => IReminderTemplateV2[]
  ) => {
    onChange((x) => ({
      ...x,
      remindersV2: sortBy(
        updateFn(x.remindersV2),
        (r) => r.relativeDistanceToDeadline
      )
    }));
  };

  if (newUx) {
    const setPre = (nextPre: IReminderTemplateV2[]) =>
      updateRemindersV2((rs) => [
        ...nextPre,
        ...rs.filter((r) => r.relativeDistanceToDeadline >= 0)
      ]);
    return (
      <div>
        <UX.SectionLabel style={{ marginTop: 0 }}>
          {t('Reminders')}
        </UX.SectionLabel>
        <UX.FieldHint style={{ marginTop: 0, marginBottom: 12 }}>
          {t(
            'Set up completion reminders to be sent before and after the task deadline.'
          )}
        </UX.FieldHint>

        <Typography variant="h6" style={{ marginTop: 0, marginBottom: 8 }}>
          {t('Pre-deadline Reminders')}
        </Typography>
        {reminders.preDeadline.map((r, i) => {
          const days = Math.round(
            Math.abs(r.relativeDistanceToDeadline) / ONE_DAY
          );
          return (
            <NewUxReminderRow key={r.id}>
              <NewUxIndex>{i + 1}</NewUxIndex>
              <ClassicBlue600>
                <TextField
                  type="number"
                  variant="outlined"
                  style={{ width: 70 }}
                  inputProps={{ min: 1 }}
                  value={days}
                  onChange={(ev) =>
                    setPre(
                      reminders.preDeadline.map((rr, idx) =>
                        idx === i
                          ? {
                              ...rr,
                              relativeDistanceToDeadline: -(+ev.target.value * ONE_DAY)
                            }
                          : rr
                      )
                    )
                  }
                />
              </ClassicBlue600>
              <span style={{ whiteSpace: 'nowrap' }}>
                {days === 1
                  ? t('day before deadline')
                  : t('days before deadline')}
              </span>
              <button
                type="button"
                aria-label={t('Remove reminder')}
                onClick={() =>
                  setPre(reminders.preDeadline.filter((_, idx) => idx !== i))
                }
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  display: 'flex',
                  padding: 2,
                  marginLeft: 'auto'
                }}
              >
                <Trash2 size={14} />
              </button>
            </NewUxReminderRow>
          );
        })}
        <NewUxAddRow
          type="button"
          onClick={() =>
            setPre([
              ...reminders.preDeadline,
              { id: shortid(), relativeDistanceToDeadline: -ONE_DAY, v: 2 }
            ])
          }
        >
          <PlusCircle size={16} /> {t('Add Reminder')}
        </NewUxAddRow>

        <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
          {t('Overdue Reminders')}
        </Typography>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 8
          }}
        >
          <UX.Toggle
            on={reminders.onDeadline}
            ariaLabel={t('Remind on deadline')}
            onChange={(on) => {
              if (on) {
                updateRemindersV2((rs) => [
                  ...rs,
                  { id: shortid(), relativeDistanceToDeadline: 0, v: 2 }
                ]);
              } else {
                updateRemindersV2((rs) =>
                  rs.filter((r) => r.relativeDistanceToDeadline !== 0)
                );
              }
            }}
          />
          <span style={{ fontSize: 13 }}>{t('Remind on deadline')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UX.Toggle
            on={!!reminders.afterDeadline.count}
            ariaLabel={t('Remind after deadline')}
            onChange={(on) => {
              if (on) {
                updateRemindersV2((rs) => [
                  ...rs,
                  ...times(DEFAULT_LATE_REMINDER_MAX).map<IReminderTemplateV2>(
                    (i) => ({
                      id: shortid(),
                      relativeDistanceToDeadline: (i + 1) * ONE_DAY,
                      v: 2
                    })
                  )
                ]);
              } else {
                updateRemindersV2((rs) =>
                  rs.filter((r) => r.relativeDistanceToDeadline <= 0)
                );
              }
            }}
          />
          <span style={{ fontSize: 13 }}>{t('Remind after deadline')}</span>
        </div>
        {!!reminders.afterDeadline.count && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <NewUxInline>
              <span>{t('Every')}</span>
              <ClassicBlue600>
                <TextField
                  type="number"
                  variant="outlined"
                  style={{ width: 70 }}
                  inputProps={{ min: 1 }}
                  value={Math.round(reminders.afterDeadline.interval / ONE_DAY)}
                  onChange={(ev) => {
                    const nextInterval = +ev.target.value;
                    updateRemindersV2((rs) => [
                      ...rs.filter((r) => r.relativeDistanceToDeadline <= 0),
                      ...times(reminders.afterDeadline.count).map<IReminderTemplateV2>(
                        (i) => ({
                          id: shortid(),
                          relativeDistanceToDeadline:
                            (i + 1) * nextInterval * ONE_DAY,
                          v: 2
                        })
                      )
                    ]);
                  }}
                />
              </ClassicBlue600>
              <span>
                {reminders.afterDeadline.interval === ONE_DAY
                  ? t('day')
                  : t('days')}
              </span>
            </NewUxInline>
            <NewUxInline>
              <span>{t('Send max')}</span>
              <ClassicBlue600>
                <TextField
                  type="number"
                  variant="outlined"
                  style={{ width: 70 }}
                  inputProps={{ min: 1, max: 20 }}
                  value={reminders.afterDeadline.count}
                  onChange={(ev) => {
                    const nextCount = +ev.target.value;
                    updateRemindersV2((rs) => [
                      ...rs.filter((r) => r.relativeDistanceToDeadline <= 0),
                      ...times(nextCount).map<IReminderTemplateV2>((i) => ({
                        id: shortid(),
                        relativeDistanceToDeadline:
                          (i + 1) * reminders.afterDeadline.interval,
                        v: 2
                      }))
                    ]);
                  }}
                />
              </ClassicBlue600>
            </NewUxInline>
          </div>
        )}
      </div>
    );
  }

  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />

      <SettingsSection>
        <FlexContainer
          direction="column"
          alignItems="flex-start"
          spacing={3}
          fullWidth
        >
          <FullWidthContainer>
            <Label label={t('Pre-deadline Reminders')} />

            <FlexContainer
              direction="column"
              alignItems="flex-start"
              spacing={2}
              fullWidth
              marginTop={2}
            >
              <RemindersV2
                value={reminders.preDeadline}
                onChange={(nextPreDeadlineReminders) => {
                  updateRemindersV2((rs) => {
                    const nextReminders = rs.filter(
                      (r) => r.relativeDistanceToDeadline >= 0
                    );
                    return [...nextPreDeadlineReminders, ...nextReminders];
                  });
                }}
              />

              <div>
                <AddReminderButtonV2
                  value={reminders.preDeadline}
                  onChange={(nextPreDeadlineReminders) => {
                    updateRemindersV2((rs) => {
                      const nextReminders = rs.filter(
                        (r) => r.relativeDistanceToDeadline >= 0
                      );
                      return [...nextPreDeadlineReminders, ...nextReminders];
                    });
                  }}
                />
              </div>
            </FlexContainer>
          </FullWidthContainer>

          <FullWidthContainer>
            <Label label={t('Overdue Reminders')} />
            <FormControlLabel
              control={
                <Switch
                  checked={reminders.onDeadline}
                  onChange={(ev) => {
                    if (ev.target.checked) {
                      updateRemindersV2((rs) => [
                        ...rs,
                        {
                          id: shortid(),
                          relativeDistanceToDeadline: 0,
                          v: 2
                        }
                      ]);
                    } else {
                      updateRemindersV2((rs) =>
                        rs.filter((r) => r.relativeDistanceToDeadline !== 0)
                      );
                    }
                  }}
                  color="primary"
                />
              }
              label={t('Remind on deadline')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!reminders.afterDeadline.count}
                  onChange={(ev) => {
                    if (ev.target.checked) {
                      updateRemindersV2((rs) => [
                        ...rs,
                        ...times(
                          DEFAULT_LATE_REMINDER_MAX
                        ).map<IReminderTemplateV2>((i) => ({
                          id: shortid(),
                          relativeDistanceToDeadline: (i + 1) * ONE_DAY,
                          v: 2
                        }))
                      ]);
                    } else {
                      updateRemindersV2((rs) =>
                        rs.filter((r) => r.relativeDistanceToDeadline <= 0)
                      );
                    }
                  }}
                  color="primary"
                />
              }
              label={t('Remind after deadline')}
            />
            {!!reminders.afterDeadline.count && (
              <FlexContainer
                direction="column"
                alignItems="flex-start"
                spacing={1}
              >
                <FlexContainer justifyContent="flex-start" spacing={1}>
                  <div>{t('Every')}</div>
                  <TextField
                    type="number"
                    value={Math.round(
                      reminders.afterDeadline.interval / ONE_DAY
                    )}
                    inputProps={{
                      min: 1,
                      style: {
                        maxWidth: 40
                      }
                    }}
                    onChange={(ev) => {
                      const nextInterval = +ev.target.value;
                      updateRemindersV2((rs) => [
                        ...rs.filter((r) => r.relativeDistanceToDeadline <= 0),
                        ...times(
                          reminders.afterDeadline.count
                        ).map<IReminderTemplateV2>((i) => ({
                          id: shortid(),
                          relativeDistanceToDeadline:
                            (i + 1) * nextInterval * ONE_DAY,
                          v: 2
                        }))
                      ]);
                    }}
                  />
                  <div>
                    {reminders.afterDeadline.interval === ONE_DAY
                      ? t('day')
                      : t('days')}
                  </div>
                </FlexContainer>
                <FlexContainer justifyContent="flex-start" spacing={1}>
                  <div>{t('Send max')}</div>
                  <TextField
                    type="number"
                    value={reminders.afterDeadline.count}
                    onChange={(ev) => {
                      const nextCount = +ev.target.value;
                      updateRemindersV2((rs) => [
                        ...rs.filter((r) => r.relativeDistanceToDeadline <= 0),
                        ...times(nextCount).map<IReminderTemplateV2>((i) => ({
                          id: shortid(),
                          relativeDistanceToDeadline:
                            (i + 1) * reminders.afterDeadline.interval,
                          v: 2
                        }))
                      ]);
                    }}
                    inputProps={{ min: 1, max: 20 }}
                  />
                </FlexContainer>
              </FlexContainer>
            )}
          </FullWidthContainer>
        </FlexContainer>
      </SettingsSection>
    </SideContainer>
  );
};
